import * as yup from 'yup';
import {
  INPUT_MAX_LENGTH,
  INPUT_MIN_LENGTH,
  INPUT_VALID_CHARS_REGEX,
  USERNAME_VALID_CHARS_REGEX,
} from './constants';
import {
  EMAIL_REG_EXP,
  PHONE_REG_EXP,
  COMPANY_MAX_LENGTH,
  COMPANY_MIN_LENGTH,
  COMPANY_VALID_CHARS_REGEX,
} from '../../shared/constants';
import { UserForm } from './types';

export const userValidationSchema: yup.ObjectSchema<UserForm> = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .matches(EMAIL_REG_EXP, 'Wrong email format.'),

  username: yup
    .string()
    .required('User name is required')
    .min(
      INPUT_MIN_LENGTH,
      `User name must be at least ${INPUT_MIN_LENGTH} characters`,
    )
    .max(
      INPUT_MAX_LENGTH,
      `User name must not exceed ${INPUT_MAX_LENGTH} characters`,
    )
    .matches(
      USERNAME_VALID_CHARS_REGEX,
      'Only letters, numbers, spaces, and underscore are allowed',
    ),

  phoneNumber: yup
    .string()
    .transform(value => (value?.trim() === '' ? undefined : value))
    .matches(
      PHONE_REG_EXP,
      'Please enter at least 10 digits (e.g., +1234567890)',
    )
    .optional(),

  companyName: yup
    .string()
    .transform(value => (value?.trim() === '' ? undefined : value))
    .min(COMPANY_MIN_LENGTH, 'Company name must be at least 2 characters')
    .max(COMPANY_MAX_LENGTH, 'Company name must be less than 100 characters')
    .matches(
      COMPANY_VALID_CHARS_REGEX,
      "Only letters, numbers, spaces, and special characters (&, ., ,, -, ', /, +) are allowed",
    )
    .optional(),

  otherInfo: yup
    .string()
    .default('')
    .max(255, 'Other info must be less than 255 characters'),

  organizationIds: yup
    .array()
    .of(yup.string().required())
    .required('At least one team must be selected')
    .min(1, 'At least one team must be selected'),

  isActive: yup.boolean().required('Status is required'),
  isInternal: yup.boolean().required('This field is required'),
  attributes: yup.array().of(yup.string().required()).default([]),
}) as yup.ObjectSchema<UserFormData>;

export const organizationValidationSchema = yup.object({
  name: yup
    .string()
    .required('Team name is required')
    .min(INPUT_MIN_LENGTH, 'Team name must be at least 3 characters')
    .max(INPUT_MAX_LENGTH, 'Team name must be less than 50 characters')
    .matches(
      INPUT_VALID_CHARS_REGEX,
      'Only letters, numbers, spaces, and underscore are allowed',
    ),

  description: yup
    .string()
    .default('')
    .max(255, 'Description must be less than 255 characters')
    .matches(
      INPUT_VALID_CHARS_REGEX,
      'Only letters, numbers, spaces, and underscore are allowed',
    ),

  orgAdminEmails: yup.array().of(yup.string().required()).default([]),
});

export type UserFormData = yup.InferType<typeof userValidationSchema>;
export type OrganizationFormData = yup.InferType<
  typeof organizationValidationSchema
>;
